import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink } from 'react-router-dom';
import {
  Box,
  List,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  ListSubheader,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ROUTE_PATH from '../../../config/routes.config';
import Iconify from '../../common/iconify.component';
import {
  CONTENT_ALIAS,
  DEFAULT_CHANNEL,
  SHOW_ADMIN_USERS,
  SHOW_CONTENT_CATEGORIES,
  SHOW_CONTENT_SHOWS,
  SHOW_CONTENT_VIDEOS,
  SHOW_OVERVIEW_DASHBOARD,
  SHOW_REPORTS_APP_SESSIONS,
  SHOW_REPORTS_LIVE_USERS,
  SHOW_REPORTS_OVERVIEW_VIDEOS,
  SHOW_REPORTS_VIDEO_WATCH_SESSIONS,
  SHOW_REPORTS_WATCH_SESSIONS,
  SHOW_SUBSCRIBERS,
  SHOW_WEB_CONTENT,
  SHOW_CONTACTS,
} from '../../../config/const.config';
import palette from '../../../theme/palette';

const StyledSubHeader = styled((props) => <ListSubheader {...props} />)(
  ({ theme }) => ({
    background: 'transparent',
    color: theme.palette.grey[200],
  })
);

const StyledNavItem = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  color: theme.palette.text.secondary,
  borderRadius: '#7B61FF',
}));

const StyledNavItemIcon = styled(ListItemIcon)({
  width: 24,
  height: 24,
  color: palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 24,
});

const NavItem = ({ title, path, icon, subNav }) => (
  <StyledNavItem
    component={RouterLink}
    to={path}
    sx={{
      '&.active': {
        color: palette.common.white,
        bgcolor: '#7B61FF',
        fontWeight: 'fontWeightBold',
      },
      height: subNav ? 36 : 48,
    }}
  >
    {!subNav && <StyledNavItemIcon>{icon}</StyledNavItemIcon>}
    <ListItemText
      disableTypography
      primary={title}
      sx={{
        color: palette.common.white,
        pl: subNav ? 6 : 0,
        overflowX: 'clip',
        textWrap: 'nowrap',
        textOverflow: 'ellipsis',
        paddingRight: 2,
      }}
    />
  </StyledNavItem>
);
NavItem.propTypes = {
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  icon: PropTypes.element,
  subNav: PropTypes.bool,
};
NavItem.defaultProps = {
  icon: <div />,
  subNav: false,
};

const NavGroup = ({ title, routePath, icon, navGroup }) => {
  const urlParts = window.location.href.split('/');
  const routePathParts = routePath.split('/');

  const initOpen =
    urlParts[urlParts.length - 2] === routePathParts[routePathParts.length - 1];

  const [open, setOpen] = useState(initOpen);
  const handleClick = () => setOpen(!open);

  return (
    <>
      <StyledNavItem onClick={handleClick}>
        <StyledNavItemIcon>{icon}</StyledNavItemIcon>
        <ListItemText
          disableTypography
          primary={title}
          sx={{
            color: palette.common.white,
            overflowX: 'clip',
            textWrap: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        />
        <StyledNavItemIcon>
          <Iconify icon={open ? 'mdi:menu-up' : 'mdi:menu-down'} />
        </StyledNavItemIcon>
      </StyledNavItem>

      <Collapse in={open} timeout="auto">
        <List disablePadding sx={{ p: 1 }}>
          {navGroup.map(
            (nav) =>
              nav.status === 1 && (
                <NavItem
                  key={nav.title}
                  title={nav.title}
                  path={`${routePath}/${nav.slug}`}
                  icon={<div />}
                  subNav
                />
              )
          )}
        </List>
      </Collapse>
    </>
  );
};
NavGroup.propTypes = {
  title: PropTypes.string.isRequired,
  routePath: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  navGroup: PropTypes.array.isRequired,
};

const NavItems = () => (
  <Box>
    <List disablePadding sx={{ p: 1 }}>
      {SHOW_OVERVIEW_DASHBOARD && (
        <NavItem
          title="Overview Dashboard"
          path={ROUTE_PATH.DASHBOARD}
          icon={<Iconify icon="ic:round-dashboard" />}
        />
      )}
      {SHOW_ADMIN_USERS && (
        <NavItem
          title="Users"
          path={ROUTE_PATH.ADMIN_USERS}
          icon={<Iconify icon="mdi:users-check" />}
        />
      )}
      {SHOW_SUBSCRIBERS && (
        <NavItem
          title="Subscribers"
          path={ROUTE_PATH.SUBSCRIBERS}
          icon={<Iconify icon="ci:users" />}
        />
      )}
    
      {SHOW_CONTACTS && (
        <NavItem
          title="Contact"
          path={ROUTE_PATH.CONTACTS}
          icon={<Iconify icon="ic:baseline-contact-mail" />}
        />
      )}
      
      <StyledSubHeader component="div">Content</StyledSubHeader>
      {!DEFAULT_CHANNEL && (
        <NavItem
          title="Channels"
          path={ROUTE_PATH.CONTENT_CHANNELS}
          icon={<Iconify icon="ic:twotone-checklist" />}
        />
      )}

      {SHOW_CONTENT_CATEGORIES && (
        <NavItem
          title={`${CONTENT_ALIAS.CATEGORY_PLURAL}`}
          path={ROUTE_PATH.CONTENT_CATEGORIES}
          icon={<Iconify icon="iconamoon:category" />}
        />
      )}

      {SHOW_CONTENT_SHOWS && (
        <NavItem
          title={`${CONTENT_ALIAS.SHOW_PLURAL}`}
          path={ROUTE_PATH.CONTENT_SHOWS}
          icon={<Iconify icon="mdi:movie-open-play-outline" />}
        />
      )}

      {SHOW_CONTENT_VIDEOS && (
        <NavItem
          title={`${CONTENT_ALIAS.VIDEO_PLURAL}`}
          path={ROUTE_PATH.CONTENT_VIDEOS}
          icon={<Iconify icon="octicon:video-16" />}
        />
      )}
      {SHOW_WEB_CONTENT && (
        <NavItem
          title={`${CONTENT_ALIAS.WEB_CONTENT}`}
          path={ROUTE_PATH.CONTENT_WEB}
          icon={<Iconify icon="mdi:web" />}
        />
      )}
      {DEFAULT_CHANNEL && (
        <NavItem
          title="Sequence"
          path={ROUTE_PATH.CONTENT_SEQUENCE}
          icon={<Iconify icon="fluent-mdl2:number-sequence" />}
        />
      )}

      {DEFAULT_CHANNEL && (
        <NavItem
          title="Settings"
          path={ROUTE_PATH.CONTENT_SETTINGS}
          icon={<Iconify icon="ant-design:setting-outlined" />}
        />
      )}

      {(SHOW_REPORTS_OVERVIEW_VIDEOS ||
        SHOW_REPORTS_LIVE_USERS ||
        SHOW_REPORTS_WATCH_SESSIONS ||
        SHOW_REPORTS_VIDEO_WATCH_SESSIONS ||
        SHOW_REPORTS_APP_SESSIONS) && (
        <StyledSubHeader component="div">Analytics Reports</StyledSubHeader>
      )}
      {SHOW_REPORTS_APP_SESSIONS && (
        <NavItem
          title="App Sessions"
          path={ROUTE_PATH.REPORTS_APP_SESSIONS}
          icon={<Iconify icon="ic:outline-list-alt" />}
        />
      )}
      {SHOW_REPORTS_OVERVIEW_VIDEOS && (
        <NavItem
          title={`Overview (${CONTENT_ALIAS.VIDEO_PLURAL})`}
          path={ROUTE_PATH.REPORTS_OVERVIEW_VIDEOS}
          icon={<Iconify icon="ic:outline-analytics" />}
        />
      )}
      {SHOW_REPORTS_LIVE_USERS && (
        <NavItem
          title="Live Users"
          path={ROUTE_PATH.REPORTS_LIVE_USERS}
          icon={<Iconify icon="ic:round-live-tv" />}
        />
      )}
      {SHOW_REPORTS_WATCH_SESSIONS && (
        <NavItem
          title="Watch Sessions"
          path={ROUTE_PATH.REPORTS_WATCH_SESSIONS}
          icon={<Iconify icon="ic:outline-list-alt" />}
        />
      )}
      {SHOW_REPORTS_VIDEO_WATCH_SESSIONS && (
        <NavItem
          title="Episode Watch Session"
          path={ROUTE_PATH.REPORTS_VIDEO_WATCH_SESSIONS}
          icon={<Iconify icon="octicon:video-16" />}
        />
      )}
    </List>
  </Box>
);

export default NavItems;
